// src/screens/superadmin/refundmanagement/RefundManagement.jsx
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Button, Table, Modal } from "react-bootstrap";
import {
  FiEye,
  FiX,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiDollarSign,
  FiUser,
  FiMail,
  FiHash,
  FiCalendar,
  FiCheck,
  FiXCircle,
  FiMoreVertical,
  FiRefreshCcw,
} from "react-icons/fi";
import AppPagination from "../../../components/general/pagination/Pagination.js";
// ── Reusing the same "pm-" styled classes/layout as Payouts Management,
// taake dono screens ka look-and-feel consistent rahe ──
import "../../../assets/css/superadmin/payoutmanagement/PayoutManagement.css";
import MasterLayout from "../../../layout/superadmin/MasterLayout";
import AppContext from "../../../context/AppContext.js";

const TABS = [
  { label: "Requested", icon: <FiClock size={14} />, status: "requested" },
  { label: "Refunded", icon: <FiCheckCircle size={14} />, status: "refunded" },
  { label: "Rejected", icon: <FiXCircle size={14} />, status: "rejected" },
];

// ── Backend Srrefundrequest record ko table/modal ke mutabiq shape mein convert karta hai ──
const mapRefund = (r) => {
  const booking = r.srrentalrequest || {};
  const customer = r.srcustomer || {};
  const rentaladmin = r.srrentaladmin || {};
  const equipment = booking.srequipmentmanagment || {};

  return {
    id: `REF-${String(r.id).padStart(4, "0")}`,
    rawId: r.id,
    bookingId: booking.id,
    equipmentName: equipment.equipmentname || "Equipment",
    customer: {
      name: customer.fullname || "Unknown Customer",
      email: customer.email || "N/A",
      image: customer.image || null,
    },
    rentaladmin: {
      name: rentaladmin.fullname || "N/A",
      email: rentaladmin.email || "N/A",
    },
    amount: `Rs ${Number(r.amount).toLocaleString("en-PK")}`,
    reason: r.reason || "No reason provided",
    date: r.created_at ? r.created_at.split("T")[0] : "",
    status: r.status,
    adminRemarksSaved: r.admin_remarks || "",
    paymentIntentId: r.stripe_payment_intent_id,
  };
};

const getStatusClass = (status) => {
  switch (status) {
    case "refunded":
      return "pm-badge-completed";
    case "requested":
      return "pm-badge-pending";
    case "rejected":
      return "pm-badge-rejected";
    default:
      return "pm-badge-pending";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "requested":
      return "Requested";
    case "refunded":
      return "Refunded";
    case "rejected":
      return "Rejected";
    default:
      return status;
  }
};

const RefundManagement = () => {
  const { baseUrl, baseUrlForFile, authToken, loggedInAdmin } = useContext(AppContext);
  const isModerator = loggedInAdmin?.issuper === 'moderator';

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requested");
  const [refundsData, setRefundsData] = useState([]);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [adminRemarks, setAdminRemarks] = useState("");
  const [processing, setProcessing] = useState(false);

  const viewAllRefunds = () => {
    setIsLoading(true);
    axios
      .get(`${baseUrl}/superadmin/refund/fetchAllRefundRequests`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const list = res.data.refunds || [];
        setRefundsData(list.map(mapRefund));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    viewAllRefunds();
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".pm-dropdown-wrapper")) setActiveDropdown(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const requestedCount = refundsData.filter((r) => r.status === "requested").length;
  const refundedCount = refundsData.filter((r) => r.status === "refunded").length;
  const rejectedCount = refundsData.filter((r) => r.status === "rejected").length;

  const STATS = [
    {
      label: "Total Requests",
      value: refundsData.length,
      sub: "all refund requests",
      iconBg: "#FFF3CD",
      iconColor: "#FDB714",
      icon: <FiRefreshCcw size={22} />,
    },
    {
      label: "Awaiting Review",
      value: requestedCount,
      sub: "need your action",
      iconBg: "#FEF9C3",
      iconColor: "#A16207",
      icon: <FiClock size={22} />,
    },
    {
      label: "Refunded",
      value: refundedCount,
      sub: "money returned",
      iconBg: "#DCFCE7",
      iconColor: "#22C55E",
      icon: <FiCheckCircle size={22} />,
    },
    {
      label: "Rejected",
      value: rejectedCount,
      sub: "declined requests",
      iconBg: "#FEE2E2",
      iconColor: "#DC2626",
      icon: <FiXCircle size={22} />,
    },
  ];

  const filtered = refundsData.filter((r) => r.status === activeTab);

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const toggleDropdown = (e, id) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const handleView = (r) => {
    setSelectedRefund(r);
    setAdminRemarks(r.adminRemarksSaved || "");
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleClose = () => {
    if (processing) return;
    setShowModal(false);
    setSelectedRefund(null);
    setAdminRemarks("");
  };

  // ── Approve → asal Stripe refund call backend par hota hai ──
  const processRefund = (refund, action) => {
    setProcessing(true);
    axios
      .post(
        `${baseUrl}/superadmin/refund/processRefund`,
        {
          id: refund.rawId,
          action,
          remarks: adminRemarks,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((res) => {
        if (!res.data.success) {
          alert(res.data.message || "Something went wrong.");
          return;
        }
        viewAllRefunds();
        handleClose();
      })
      .catch((err) => {
        console.log(err);
        alert("Something went wrong while processing this refund.");
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleApprove = () => {
    if (!selectedRefund) return;
    processRefund(selectedRefund, "approve");
  };

  const handleReject = () => {
    if (!selectedRefund) return;
    processRefund(selectedRefund, "reject");
  };

  return (
    <MasterLayout>
      {/* ── Page Header ── */}
      <div className="pm-header">
        <h1>Refund Requests</h1>
        <p>Review customer cancellations and process Stripe refunds.</p>
      </div>

      {/* ── Stats Cards ── */}
      <div className="pm-stats-grid mb-4">
        {STATS.map((s, i) => (
          <div className="pm-stat-card" key={i}>
            <div className="pm-stat-card-top">
              <div>
                <p className="pm-stat-label">{s.label}</p>
                <h3 className="pm-stat-value">{s.value}</h3>
                <p className="pm-stat-sub">{s.sub}</p>
              </div>
              <div
                className="pm-stat-icon"
                style={{ background: s.iconBg, color: s.iconColor }}
              >
                {s.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="pm-outer-tabs-wrap">
        <div className="pm-outer-tabs">
          {TABS.map((tab) => {
            const count = refundsData.filter((r) => r.status === tab.status).length;
            return (
              <button
                key={tab.status}
                className={`pm-outer-tab ${activeTab === tab.status ? "pm-outer-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab.status);
                  setCurrentPage(1);
                }}
              >
                {tab.icon}
                {tab.label}
                <span className="pm-outer-tab-count">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Table Card ── */}
      <div className="pm-table-card">
        <div className="pm-table-heading">
          <h2 className="pm-table-title">
            {activeTab === "requested" && "Refund Requests Awaiting Review"}
            {activeTab === "refunded" && "Refunded"}
            {activeTab === "rejected" && "Rejected Refund Requests"}
          </h2>
        </div>

        <div className="pm-table-wrapper">
          <Table className="pm-table">
            <thead>
              <tr>
                <th className="pm-col-id">REFUND ID</th>
                <th className="pm-col-seller">CUSTOMER</th>
                <th>EQUIPMENT</th>
                <th className="pm-col-amount">AMOUNT</th>
                <th className="pm-col-date">DATE</th>
                <th className="pm-col-status">STATUS</th>
                <th className="pm-col-action">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="pm-empty-row">
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="pm-empty-row">
                    No refund requests found.
                  </td>
                </tr>
              ) : (
                paginatedData.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="pm-payout-id">{r.id}</span>
                    </td>
                    <td>
                      <div className="pm-seller-cell">
                        <img
                          src={`${baseUrlForFile}/${r.customer.image}`}
                          alt=""
                          className="pm-avatar-img"
                        />
                        <div className="pm-seller-info">
                          <div className="pm-seller-name">{r.customer.name}</div>
                          <div className="pm-seller-email">{r.customer.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{r.equipmentName}</td>
                    <td>
                      <span className="pm-amount net">{r.amount}</span>
                    </td>
                    <td>
                      <span className="pm-date-col">{r.date}</span>
                    </td>
                    <td>
                      <span className={`pm-badge ${getStatusClass(r.status)}`}>
                        <span className="pm-badge-dot"></span>
                        {getStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="pm-text-center">
                      <div className="pm-dropdown-wrapper">
                        <button
                          className="pm-dots-btn"
                          onClick={(e) => toggleDropdown(e, r.id)}
                        >
                          <FiMoreVertical size={18} />
                        </button>
                        {activeDropdown === r.id && (
                          <div className="pm-custom-dropdown-menu">
                            <button
                              className="pm-custom-dropdown-item"
                              onClick={() => handleView(r)}
                            >
                              <FiEye size={15} className="pm-item-view" /> View
                              Detail
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <AppPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filtered.length}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* ════ REFUND DETAIL MODAL ════ */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        className="pm-detail-modal"
      >
        {selectedRefund && (
          <>
            <div className="pm-modal-header">
              <div className="pm-modal-header-left">
                <h5>Refund Request Detail</h5>
                <span>Review before approving or rejecting</span>
              </div>
              <Button className="pm-modal-close" onClick={handleClose}>
                <FiX />
              </Button>
            </div>

            <Modal.Body className="pm-modal-body">
              <div className="pm-modal-status-bar">
                <div className="sb-left">
                  <FiHash size={15} color="#FDB714" />
                  <span className="sb-id">{selectedRefund.id}</span>
                  <span className={`pm-badge ${getStatusClass(selectedRefund.status)}`}>
                    <span className="pm-badge-dot"></span>
                    {getStatusLabel(selectedRefund.status)}
                  </span>
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  <FiCalendar size={13} style={{ marginRight: 5 }} />
                  {selectedRefund.date}
                </div>
              </div>

              <p className="pm-section-title">
                <FiUser size={14} /> Customer
              </p>
              <div className="pm-modal-seller-strip">
                <img
                  src={`${baseUrlForFile}/${selectedRefund.customer.image}`}
                  alt=""
                  className="pm-avatar-img pm-avatar-img-lg"
                />
                <div style={{ flex: 1 }}>
                  <div className="ms-name">{selectedRefund.customer.name}</div>
                  <div className="ms-email">
                    <FiMail size={11} style={{ marginRight: 4 }} />
                    {selectedRefund.customer.email}
                  </div>
                </div>
              </div>

              <p className="pm-section-title">
                <FiHash size={14} /> Booking Details
              </p>
              <div className="pm-info-grid">
                <div className="pm-info-item">
                  <label>Booking ID</label>
                  <span>#{selectedRefund.bookingId}</span>
                </div>
                <div className="pm-info-item">
                  <label>Equipment</label>
                  <span>{selectedRefund.equipmentName}</span>
                </div>
                <div className="pm-info-item">
                  <label>Rental Admin</label>
                  <span>{selectedRefund.rentaladmin.name}</span>
                </div>
                <div className="pm-info-item">
                  <label>Stripe Payment ID</label>
                  <span style={{ fontSize: 11 }}>{selectedRefund.paymentIntentId}</span>
                </div>
              </div>

              <p className="pm-section-title">
                <FiAlertCircle size={14} /> Customer's Cancellation Reason
              </p>
              <div className="pm-notes-box">
                <span>{selectedRefund.reason}</span>
              </div>

              <p className="pm-section-title">
                <FiDollarSign size={14} /> Refund Amount
              </p>
              <div className="pm-financials-box">
                <div className="pm-fin-row total">
                  <span>Amount to Refund</span>
                  <span>{selectedRefund.amount}</span>
                </div>
              </div>

              <p className="pm-section-title">
                <FiAlertCircle size={14} /> Admin Remarks
              </p>
              <textarea
                className="pm-remarks-textarea"
                rows={3}
                placeholder="Add remarks (optional)"
                value={adminRemarks}
                onChange={(e) => setAdminRemarks(e.target.value)}
                disabled={selectedRefund.status !== "requested"}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: "1px solid #E5E5E5",
                  padding: 10,
                  fontSize: 13,
                }}
              />
            </Modal.Body>

            {selectedRefund.status === "requested" && (
              isModerator ? (
                <div style={{ padding: "16px 24px", borderTop: "1px solid #F0F0F0", fontSize: 13, color: "#999" }}>
                  View only — only a superadmin can approve or reject refunds.
                </div>
              ) : (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "16px 24px",
                  borderTop: "1px solid #F0F0F0",
                }}
              >
                <Button
                  className="pm-accept-btn"
                  style={{ flex: 1 }}
                  onClick={handleApprove}
                  disabled={processing}
                >
                  <FiCheck size={15} />{" "}
                  {processing ? "Processing..." : "Approve & Refund via Stripe"}
                </Button>
                <Button
                  className="pm-reject-btn"
                  style={{ flex: 1 }}
                  onClick={handleReject}
                  disabled={processing}
                >
                  <FiXCircle size={15} /> Reject
                </Button>
              </div>
              )
            )}
          </>
        )}
      </Modal>
    </MasterLayout>
  );
};

export default RefundManagement;
